'use client';
function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import * as React from 'react';
import useEnhancedEffect from '@mui/utils/useEnhancedEffect';
import { getThemeProps } from "../useThemeProps/index.js";
import useTheme from "../useThemeWithoutDefault/index.js";
// TODO React 17: Remove `useMediaQueryOld` once React 17 support is removed
function useMediaQueryOld(query, defaultMatches, matchMedia, ssrMatchMedia, noSsr) {
    var _React_useState = _sliced_to_array(React.useState(function() {
        if (noSsr && matchMedia) {
            return matchMedia(query).matches;
        }
        if (ssrMatchMedia) {
            return ssrMatchMedia(query).matches;
        }
        // Once the component is mounted, we rely on the
        // event listeners to return the correct matches value.
        return defaultMatches;
    }), 2), match = _React_useState[0], setMatch = _React_useState[1];
    useEnhancedEffect(function() {
        if (!matchMedia) {
            return undefined;
        }
        var queryList = matchMedia(query);
        var updateMatch = function() {
            setMatch(queryList.matches);
        };
        updateMatch();
        queryList.addEventListener('change', updateMatch);
        return function() {
            queryList.removeEventListener('change', updateMatch);
        };
    }, [
        query,
        matchMedia
    ]);
    return match;
}
// See https://github.com/mui/material-ui/issues/41190#issuecomment-2040873379 for why
var safeReact = _object_spread({}, React);
var maybeReactUseSyncExternalStore = safeReact.useSyncExternalStore;
function useMediaQueryNew(query, defaultMatches, matchMedia, ssrMatchMedia, noSsr) {
    var getDefaultSnapshot = React.useCallback(function() {
        return defaultMatches;
    }, [
        defaultMatches
    ]);
    var getServerSnapshot = React.useMemo(function() {
        if (noSsr && matchMedia) {
            return function() {
                return matchMedia(query).matches;
            };
        }
        if (ssrMatchMedia !== null) {
            var matches = ssrMatchMedia(query).matches;
            return function() {
                return matches;
            };
        }
        return getDefaultSnapshot;
    }, [
        getDefaultSnapshot,
        query,
        ssrMatchMedia,
        noSsr,
        matchMedia
    ]);
    var _React_useMemo = _sliced_to_array(React.useMemo(function() {
        if (matchMedia === null) {
            return [
                getDefaultSnapshot,
                function() {
                    return function() {};
                }
            ];
        }
        var mediaQueryList = matchMedia(query);
        return [
            function() {
                return mediaQueryList.matches;
            },
            function(notify) {
                mediaQueryList.addEventListener('change', notify);
                return function() {
                    mediaQueryList.removeEventListener('change', notify);
                };
            }
        ];
    }, [
        getDefaultSnapshot,
        matchMedia,
        query
    ]), 2), getSnapshot = _React_useMemo[0], subscribe = _React_useMemo[1];
    var match = maybeReactUseSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    return match;
}
// eslint-disable-next-line @typescript-eslint/naming-convention
export function unstable_createUseMediaQuery() {
    var params = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    var themeId = params.themeId;
    return function useMediaQuery(queryInput) {
        var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        var theme = useTheme();
        if (theme && themeId) {
            theme = theme[themeId] || theme;
        }
        // Wait for jsdom to support the match media feature.
        // All the browsers MUI support have this built-in.
        // This defensive check is here for simplicity.
        // Most of the time, the match media logic isn't central to people tests.
        var supportMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';
        var _getThemeProps = getThemeProps({
            name: 'MuiUseMediaQuery',
            props: options,
            theme: theme
        }), _getThemeProps_defaultMatches = _getThemeProps.defaultMatches, defaultMatches = _getThemeProps_defaultMatches === void 0 ? false : _getThemeProps_defaultMatches, _getThemeProps_matchMedia = _getThemeProps.matchMedia, matchMedia = _getThemeProps_matchMedia === void 0 ? supportMatchMedia ? window.matchMedia : null : _getThemeProps_matchMedia, _getThemeProps_ssrMatchMedia = _getThemeProps.ssrMatchMedia, ssrMatchMedia = _getThemeProps_ssrMatchMedia === void 0 ? null : _getThemeProps_ssrMatchMedia, _getThemeProps_noSsr = _getThemeProps.noSsr, noSsr = _getThemeProps_noSsr === void 0 ? false : _getThemeProps_noSsr;
        if (process.env.NODE_ENV !== 'production') {
            if (typeof queryInput === 'function' && theme === null) {
                console.error([
                    'MUI: The `query` argument provided is invalid.',
                    'You are providing a function without a theme in the context.',
                    'One of the parent elements needs to use a ThemeProvider.'
                ].join('\n'));
            }
        }
        var query = typeof queryInput === 'function' ? queryInput(theme) : queryInput;
        query = query.replace(/^@media( ?)/m, '');
        var useMediaQueryImplementation = maybeReactUseSyncExternalStore !== undefined ? useMediaQueryNew : useMediaQueryOld;
        var match = useMediaQueryImplementation(query, defaultMatches, matchMedia, ssrMatchMedia, noSsr);
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            React.useDebugValue({
                query: query,
                match: match
            });
        }
        return match;
    };
}
var useMediaQuery = unstable_createUseMediaQuery();
export default useMediaQuery;
